// ============================================================================
// CANCUN100 — RelojPicker.js  (APP 2/05 · F1: reloj con manecillas, estilo Google)
// ----------------------------------------------------------------------------
// Botón que muestra la hora («10:30 a. m.» o «Elegir hora»). Al tocarlo abre un dial: primero horas
// (12 posiciones), luego minutos (de 5 en 5), a. m./p. m., «Borrar» y «Listo». Guarda 'HH:MM' en 24 h.
// Se usa en las horas de Servicios Personalizados (transporte ida/regreso, tour privado, vuelo).
// Uso: <RelojPicker value="14:30" onChange={(v) => …} label="Hora pickup" required faltante />
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { RADIO, RADIO_TARJETA } from './tema';
import { ACENTO, TINTE } from './Stepper';

const pad = (n) => String(n).padStart(2, '0');
// '14:30' → '2:30 p. m.'
export const hora12 = (v) => {
  if (!v || !/^\d{1,2}:\d{2}/.test(v)) return '';
  let [h, m] = v.split(':').map(Number);
  const ap = h >= 12 ? 'p. m.' : 'a. m.';
  h = h % 12 || 12;
  return `${h}:${pad(m)} ${ap}`;
};
// ¿Cae entre las 11 p. m. y las 6 a. m.? (horario especial de transportes)
export const esNocturna = (v) => { if (!v) return false; const hh = parseInt(v.split(':')[0], 10); return hh >= 23 || hh < 6; };

export default function RelojPicker({ value, onChange, label, required, faltante, disabled, id }) {
  const [abierto, setAbierto] = useState(false);
  const [modo, setModo] = useState('h');
  const [h, setH] = useState(9); const [m, setM] = useState(0); const [pm, setPm] = useState(false);
  const caja = useRef(null);

  const abrir = () => {
    if (disabled) return;
    if (abierto) { setAbierto(false); return; }
    let hh = 9, mm = 0, p = false;
    if (value && /^\d{1,2}:\d{2}/.test(value)) { [hh, mm] = value.split(':').map(Number); p = hh >= 12; hh = hh % 12 || 12; mm = Math.round(mm / 5) * 5 % 60; }
    setH(hh); setM(mm); setPm(p); setModo('h'); setAbierto(true);
  };
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);
  const listo = () => { const H = (h % 12) + (pm ? 12 : 0); onChange(`${pad(H)}:${pad(m)}`); setAbierto(false); };
  const borrar = () => { onChange(''); setAbierto(false); };

  const R = 86, centro = 118;
  const esH = modo === 'h';
  const vals = esH ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const sel = esH ? h : m;
  const idx = esH ? (h % 12) : Math.round(m / 5) % 12;
  const deg = idx * 30 + 180;
  const btnPie = (txt, onClick, primario, dis) => (
    <button type="button" onClick={onClick} disabled={dis} style={{ height: 38, padding: '0 16px', borderRadius: RADIO, border: primario ? 0 : '1px solid var(--au-glass-border-strong)', background: primario ? ACENTO : 'transparent', color: primario ? '#fff' : 'var(--au-text-secondary)', font: 'inherit', fontWeight: 700, cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.5 : 1 }}>{txt}</button>
  );
  const seg = (on, onClick, txt, chico) => (
    <button type="button" aria-pressed={on} onClick={onClick} style={{ font: 'inherit', border: chico ? '1px solid var(--au-glass-border-strong)' : 0, borderRadius: chico ? 7 : 8, padding: chico ? 0 : '0 6px', height: chico ? 28 : 'auto', width: chico ? 46 : 'auto', fontSize: chico ? 12 : 34, fontWeight: chico ? 700 : 800, background: on ? (chico ? ACENTO : TINTE) : 'transparent', color: on ? (chico ? '#fff' : ACENTO) : 'var(--au-text-secondary)', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>{txt}</button>
  );
  return (
    <div ref={caja} style={{ marginBottom: 12, position: 'relative', minWidth: 0 }}>
      {label && <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--au-text-faint)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--au-error-fg)' }}> *</span>}</div>}
      <button id={id} type="button" onClick={abrir} disabled={disabled} aria-expanded={abierto} aria-haspopup="dialog"
        style={{ height: 40, width: '100%', boxSizing: 'border-box', border: `1px solid ${faltante ? 'var(--au-error-fg)' : 'var(--au-glass-border-strong)'}`, borderRadius: RADIO, background: 'var(--au-bg-layer-2)', color: value ? 'var(--au-text-primary)' : 'var(--au-text-faint)', font: 'inherit', fontSize: 14, textAlign: 'left', padding: '0 12px', cursor: disabled ? 'default' : 'pointer', fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span>{value ? hora12(value) : 'Elegir hora'}</span><span aria-hidden="true" style={{ opacity: 0.5, fontSize: 14 }}>🕒</span>
      </button>
      {abierto && (
        <div role="dialog" aria-label="Elegir hora" style={{ position: 'absolute', zIndex: 60, top: '100%', left: 0, marginTop: 6, width: 272, maxWidth: 'calc(100vw - 32px)', border: '1px solid var(--au-glass-border-strong)', borderRadius: RADIO_TARJETA, background: 'var(--au-bg-layer-2)', padding: 14, boxShadow: '0 12px 32px rgba(0,0,0,.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 30, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--au-text-primary)' }}>
              {seg(esH, () => setModo('h'), h)}<span>:</span>{seg(!esH, () => setModo('m'), pad(m))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {seg(!pm, () => setPm(false), 'a. m.', true)}{seg(pm, () => setPm(true), 'p. m.', true)}
            </div>
          </div>
          <div style={{ position: 'relative', width: 236, height: 236, margin: '0 auto', borderRadius: '50%', background: TINTE }}>
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 2, height: R, background: ACENTO, transformOrigin: '50% 0', transform: `rotate(${deg}deg)`, borderRadius: 2 }} />
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 8, height: 8, margin: '-4px 0 0 -4px', borderRadius: '50%', background: ACENTO }} />
            {vals.map((v, i) => {
              const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
              const x = centro + R * Math.cos(a), y = centro + R * Math.sin(a);
              const on = v === sel;
              return (
                <button key={v} type="button" aria-pressed={on} onClick={() => { if (esH) { setH(v); setModo('m'); } else setM(v); }}
                  style={{ position: 'absolute', left: x, top: y, width: 38, height: 38, margin: '-19px 0 0 -19px', borderRadius: '50%', border: 0, background: on ? ACENTO : 'transparent', color: on ? '#fff' : 'var(--au-text-primary)', font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>
                  {esH ? v : pad(v)}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--au-text-faint)', textAlign: 'center', marginTop: 6 }}>{esH ? 'Toca la hora' : 'Toca los minutos'}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
            {btnPie('Borrar', borrar, false)}{btnPie('Listo', listo, true)}
          </div>
        </div>
      )}
    </div>
  );
}
